import React from 'react';
import {Apartment as ApartmentType} from '../api.apartments';
import GenericListItem from './GenericListItem';
import CalendarIcon from './icons/CalendarIcon';
import InfoIcon from './icons/InfoIcon';
import AppButton from './AppButton';
import ButtonRow from './ButtonRow';
import { useLang } from '../i18n/LanguageContext';
import { apartmentsText } from '../i18n/pages/apartments';

type ApartmentListItemProps = {
    apartment: ApartmentType;
    compact: boolean;
    onViewReservations: (apartmentId: number) => void;
    onOpenDetail: (apartment: ApartmentType) => void;
    /** Onboarding "connect calendar" link, shown under the details of a source-less apartment. */
    calendarCta?: React.ReactNode;
};

const ApartmentListItem: React.FC<ApartmentListItemProps> = ({
                                                                 apartment,
                                                                 compact,
                                                                 onViewReservations,
                                                                 onOpenDetail,
                                                                 calendarCta,
                                                             }) => {
  const { lang } = useLang();
  const t = apartmentsText[lang];
    const compactMain = (
        <>
            <div
                style={{
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: apartment.isActive ? '#1a202c' : '#a0aec0',
                    lineHeight: 1.15,
                }}
            >
                {apartment.name}
            </div>
            <div
                style={{
                    color: '#718096',
                    fontSize: '0.74rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.15,
                }}
            >
                {apartment.address}
            </div>
            <div style={{color: '#718096', fontSize: '0.66rem', marginTop: 1, lineHeight: 1.1}}>
                {t.tableCapacity}: {apartment.capacity}
            </div>
            {apartment.calendarSyncOk === false && (
                <div style={{color: '#c53030', fontSize: '0.66rem', marginTop: 2, lineHeight: 1.1, fontWeight: 700}}>
                    ⚠️ {t.calendarSyncError}
                </div>
            )}
            {calendarCta && <div style={{marginTop: 4}}>{calendarCta}</div>}
        </>
    );

    const compactActions = (
        <ButtonRow compact>
            <AppButton
                size="standard"
                variant="green"
                iconOnly
                title="View Reservations"
                onClick={() => onViewReservations(apartment.id!)}
            >
                <CalendarIcon/>
            </AppButton>
            <AppButton
                size="standard"
                variant="blue"
                iconOnly
                title="Detail"
                onClick={() => onOpenDetail(apartment)}
            >
                <InfoIcon/>
            </AppButton>
        </ButtonRow>
    );

    const fullBadge = (
        <span
            style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: 999,
                fontSize: '0.8rem',
                fontWeight: 700,
                position: 'absolute',
                top: 16,
                right: 16,
                background: apartment.isActive ? '#c6f6d5' : '#fed7d7',
                color: apartment.isActive ? '#22543d' : '#742a2a',
            }}
        >
      {apartment.isActive ? `✓ ${t.statusActive}` : `✗ ${t.statusInactive}`}
    </span>
    );

    const fullMain = (
        <>
            <div style={{
                fontSize: '1.05rem',
                fontWeight: 700,
                marginBottom: 6,
                color: apartment.isActive ? '#1a202c' : '#a0aec0'
            }}>
                {apartment.name}
            </div>
            <div style={{fontSize: '0.95rem', color: '#4a5568', marginBottom: 4}}>{t.tableAddress}: {apartment.address}</div>
            <div style={{fontSize: '0.95rem', color: '#4a5568', marginBottom: 4}}>{t.tableCapacity}: {apartment.capacity}</div>
            <div style={{fontSize: '0.95rem', color: '#4a5568', marginBottom: 4}}>
                {t.tableContact}: {apartment.assignedContact ? apartment.assignedContact.name : t.contactYou}
            </div>
            {apartment.calendarSyncOk === false && (
                <div style={{fontSize: '0.85rem', color: '#c53030', fontWeight: 700, marginBottom: 4}}>
                    ⚠️ {t.calendarSyncError}
                </div>
            )}
            {calendarCta && <div style={{marginBottom: 8}}>{calendarCta}</div>}
            {apartment.description &&
                <div style={{fontSize: '0.95rem', color: '#4a5568', marginBottom: 8}}>{apartment.description}</div>}
        </>
    );

    const fullActions = (
        <ButtonRow>
            <AppButton size="standard" variant="green" onClick={() => onViewReservations(apartment.id!)}>
                {t.reservations}
            </AppButton>
            <AppButton size="standard" variant="blue" onClick={() => onOpenDetail(apartment)}>
                Detail
            </AppButton>
        </ButtonRow>
    );

    return (
        <GenericListItem
            compact={compact}
            compactAccentColor={apartment.isActive ? '#48bb78' : '#fc8181'}
            compactMain={compactMain}
            compactActions={compactActions}
            fullBadge={fullBadge}
            fullMain={fullMain}
            fullActions={fullActions}
        />
    );
};

export default ApartmentListItem;

